/**
 * Shared definition of the `localStorage` entry that holds the user's side nav
 * state (collapsed/expanded, width, and the user menu ordering).
 *
 * This lives here — rather than in either nav package — because
 * `@atlassian/global-side-navigation` (GSN) and
 * `@atlassian/global-top-navigation` (GTN) must agree on it *exactly*. GTN owns
 * the side nav toggle button in the top bar while GSN owns the side nav itself,
 * so if the two compute different keys the navs disagree about the collapsed
 * state after a reload.
 */

/**
 * The un-suffixed key prefix.
 *
 * Prefer {@link getSideNavStateLocalStorageKey} — the real entry is always
 * scoped by app. This is exported only for packages that need the raw prefix
 * (for example, to clear every app's state).
 */
export const NAV_STATE_LS_KEY = 'nav-platform-sidebar-state';

/**
 * Builds the app-scoped `localStorage` key for the side nav state.
 *
 * @param app - the app identifier (e.g. `'jira'`, `'confluence'`).
 * @returns the key, e.g. `nav-platform-sidebar-state-jira`.
 *
 * @example
 * ```ts
 * getSideNavStateLocalStorageKey('jira'); // 'nav-platform-sidebar-state-jira'
 * ```
 */
export function getSideNavStateLocalStorageKey(app: string): string {
	return `${NAV_STATE_LS_KEY}-${app}`;
}

/**
 * Builds the app-scoped `localStorage` key that stores ONLY the side nav's
 * `defaultCollapsed` boolean (as a stringified boolean, e.g. `'true'`).
 *
 * This is intentionally separate from {@link getSideNavStateLocalStorageKey}:
 * GTN only needs to persist the collapsed boolean and does so under a dedicated
 * `-default-collapsed` suffix, rather than reading/writing GSN's richer JSON blob
 * (which also holds width and user-menu ordering). GSN will be migrated to read
 * `defaultCollapsed` from this key in a later work package, after which the two
 * packages will be aligned again.
 *
 * @param app - the app identifier (e.g. `'jira'`, `'confluence'`).
 * @returns the key, e.g. `nav-platform-sidebar-state-jira-default-collapsed`.
 *
 * @example
 * ```ts
 * getSideNavDefaultCollapsedLocalStorageKey('jira');
 * // 'nav-platform-sidebar-state-jira-default-collapsed'
 * ```
 */
export function getSideNavDefaultCollapsedLocalStorageKey(app: string): string {
	return `${getSideNavStateLocalStorageKey(app)}-default-collapsed`;
}

/**
 * Property key for the side nav's collapsed state.
 *
 * Used both as a field of `userSideNavData` inside the `localStorage` blob and
 * as the `key` of a `SettingsDisplayPropertyInput` when the same preference is
 * persisted over the network, so both stores must use this one spelling.
 */
export const DEFAULT_COLLAPSED_KEY = 'defaultCollapsed' as const;

/**
 * Property key for the side nav's width. See {@link DEFAULT_COLLAPSED_KEY}.
 */
export const DEFAULT_WIDTH_KEY = 'defaultWidth' as const;
